import { useEffect } from 'react';
import { getTabId } from './tabId';
import type { SessionKillReason } from '../api/client';

const CHANNEL = 'transpay-session';

type TabMessage = { type: 'hello' | 'kill'; tabId: string };

/**
 * Same-browser multi-tab guard (FR-1). When a second tab/window of the same
 * browser is open on the dashboard, BOTH tabs must be logged out and re-login
 * required (product decision). Detection is instant via BroadcastChannel:
 *   - A newly-mounted tab posts `hello`.
 *   - Any other authenticated tab that hears a `hello`/`kill` from a DIFFERENT
 *     tabId runs onKill('concurrent_tab') and re-broadcasts `kill` so every tab
 *     ends. onKill denylists the shared cookie server-side so re-login is forced.
 * A page REFRESH does not self-trigger: tabId is stable across refresh
 * (sessionStorage) and the refreshing tab is momentarily the only one on the
 * channel. Browsers without BroadcastChannel fall back to the server's
 * one-connection-per-jti SSE backstop.
 */
export function useTabGuard(
  enabled: boolean,
  onKill: (reason: SessionKillReason) => void,
): void {
  useEffect(() => {
    if (!enabled) return;
    if (typeof BroadcastChannel === 'undefined') return;

    const myTabId = getTabId();
    const ch = new BroadcastChannel(CHANNEL);
    let fired = false;

    const trigger = (): void => {
      if (fired) return;
      fired = true;
      onKill('concurrent_tab');
    };

    ch.onmessage = (ev: MessageEvent<TabMessage>) => {
      const msg = ev.data;
      if (!msg || msg.tabId === myTabId) return; // ignore our own messages
      if (msg.type === 'hello' || msg.type === 'kill') {
        // A different tab exists -> end this one too, and make sure every tab
        // learns it must die.
        try {
          ch.postMessage({ type: 'kill', tabId: myTabId } satisfies TabMessage);
        } catch {
          /* channel closing */
        }
        trigger();
      }
    };

    // Announce presence so any pre-existing tab detects the conflict.
    try {
      ch.postMessage({ type: 'hello', tabId: myTabId } satisfies TabMessage);
    } catch {
      /* noop */
    }

    return () => ch.close();
  }, [enabled, onKill]);
}
